<template>
  <div class="vcv-tiptap-menu">
    <template v-for="(item, index) in items">
      <div v-if="item.type === 'divider'" :key="`divider${index}`" class="divider"></div>

      <menu-item v-else :key="index" v-bind="item" @set-link="setLink($event)" @add-variable="addVariable($event)" />
    </template>

    <slot name="toolbar"></slot>
  </div>
</template>

<script>
import MenuItem from './VcvTiptapMenuItem';

export default {
  components: {
    MenuItem
  },

  props: {
    editor: {
      type: Object,
      required: true
    },

    variables: {
      type: [Array],
      default: () => []
    },

    // items in array. For example ['bold', 'italic', 'list' ...]
    toolbar: {
      type: [Array, String],
      default: () => []
    }
  },

  computed: {
    items() {
      const allButtons = [
        {
          icon: 'bold',
          title: this.$tr('editor.bold'),
          action: () => this.editor.chain().focus().toggleBold().run(),
          isActive: () => this.editor.isActive('bold')
        },
        {
          icon: 'italic',
          title: this.$tr('editor.italic'),
          action: () => this.editor.chain().focus().toggleItalic().run(),
          isActive: () => this.editor.isActive('italic')
        },
        // {
        //   icon: 'strikethrough',
        //   title: 'Strike',
        //   action: () => this.editor.chain().focus().toggleStrike().run(),
        //   isActive: () => this.editor.isActive('strike')
        // },
        {
          icon: 'underline',
          title: this.$tr('editor.underline'),
          action: () => this.editor.chain().focus().toggleUnderline().run(),
          isActive: () => this.editor.isActive('underline')
        },
        {
          icon: 'format-clear',
          title: this.$tr('editor.clear-format'),
          action: () => this.editor.chain().focus().clearNodes().unsetAllMarks().run()
        },
        // {
        //   icon: 'code-view',
        //   title: 'Code',
        //   action: () => this.editor.chain().focus().toggleCode().run(),
        //   isActive: () => this.editor.isActive('code')
        // },
        {
          icon: 'list-unordered',
          title: this.$tr('editor.bullet-list'),
          action: () => this.editor.chain().focus().toggleBulletList().run(),
          isActive: () => this.editor.isActive('bulletList')
        },
        {
          icon: 'list-ordered',
          title: this.$tr('editor.ordered-list'),
          action: () => this.editor.chain().focus().toggleOrderedList().run(),
          isActive: () => this.editor.isActive('orderedList')
        },
        // {
        //   icon: 'separator',
        //   title: 'Horizontal Rule',
        //   action: () => this.editor.chain().focus().setHorizontalRule().run()
        // },
        {
          type: 'divider'
        },
        // {
        //   icon: 'double-quotes-l',
        //   title: 'Blockquote',
        //   action: () => this.editor.chain().focus().toggleBlockquote().run(),
        //   isActive: () => this.editor.isActive('blockquote')
        // },
        {
          icon: 'link',
          title: this.$tr('editor.insert-link'),
          action: () => {
            const url = window.prompt('URL');

            if (url) {
              this.editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
            }
          }
        },
        {
          icon: 'image-line',
          title: this.$tr('editor.insert-image')
        },
        {
          icon: 'file-upload-line',
          title: this.$tr('editor.insert-file')
        },
        {
          type: 'divider'
        },
        {
          icon: 'table-line',
          title: this.$tr('editor.insert-table'),
          action: () => this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        },
        {
          icon: 'insert-row-bottom',
          title: this.$tr('editor.insert-table-row'),
          action: () => this.editor.chain().focus().addRowAfter().run()
        },
        {
          icon: 'delete-row',
          title: this.$tr('editor.delete-table-row'),
          action: () => this.editor.chain().focus().deleteRow().run()
        },
        {
          icon: 'insert-column-right',
          title: this.$tr('editor.insert-table-column'),
          action: () => this.editor.chain().focus().addColumnAfter().run()
        },
        {
          icon: 'delete-column',
          title: this.$tr('editor.delete-table-column'),
          action: () => this.editor.chain().focus().deleteColumn().run()
        },
        {
          icon: 'merge-cells-horizontal',
          title: this.$tr('editor.merge-table-cells'),
          action: () => this.editor.chain().focus().mergeCells().run()
        },
        {
          icon: 'split-cells-horizontal',
          title: this.$tr('editor.split-table-cells'),
          action: () => this.editor.chain().focus().splitCell().run()
        },
        {
          icon: 'layout-row-fill',
          title: this.$tr('editor.table-header-style'),
          action: () => this.editor.chain().focus().toggleHeaderRow().run()
        }
      ];

      let items = this.toolbar === 'all' ? allButtons : allButtons.filter(x => this.toolbar.includes(x.icon));

      if (this.variables.length > 0) {
        items.push({ type: 'divider' });
        items.push({
          icon: 'input-method-line',
          title: this.$tr('variables.add-variable'),
          variables: this.variables
        });
      }

      return items;
    }
  },

  methods: {
    setLink({ href, fileType }) {
      if (fileType === 'image') {
        this.editor.commands.setImage({ src: href });
      } else if (fileType === 'file') {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: href })
          .command(({ tr }) => {
            tr.insertText(`${this.$tr('editor.uploadFile')}`);
            return true;
          })
          .run();
      }
    },

    addVariable(variableName) {
      this.editor.chain().focus().insertContent(`{{${variableName}}}`).run();
    }
  }
};
</script>

<style lang="scss">
.vcv-tiptap-menu {
  display: flex;
  align-items: center;
  padding: 10px;
  border-top: 1px solid $grey-light;
  border-left: 1px solid $grey-light;
  border-right: 1px solid $grey-light;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
}

.divider {
  width: 1px;
  height: 1.25rem;
  background-color: $grey-light;
  margin-left: 0.5rem;
  margin-right: 0.75rem;
}
</style>
